import { Header } from "@/components/Header";
import { NorthCard } from "@/components/NorthCard";
import { CMACard } from "@/components/CMACard";
import { BuyerReportCard } from "@/components/BuyerReportCard";
import { CreateCMADialog } from "@/components/CreateCMADialog";
import { CreateBuyerReportDialog } from "@/components/CreateBuyerReportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Plus, AlertTriangle, Loader2, Eye, Pencil, Link as LinkIcon, Trash2, Globe, X, ArrowRightLeft, ChevronDown, SlidersHorizontal, CheckSquare, EyeOff, Check } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreateNorthDialog } from "@/components/CreateNorthDialog";
import { OverLimitDialog } from "@/components/OverLimitDialog";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useDashboardDataPaginated } from "@/hooks/useDashboardDataPaginated";
import { useCMAListQuery } from "@/hooks/useCMAData";
import { useBuyerReportListQuery } from "@/hooks/useBuyerReportData";
import { useTeamUsageQuery } from "@/hooks/useTeamUsageQuery";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useToast } from "@/hooks/use-toast";
import { AnnouncementWidget } from "@/components/AnnouncementWidget";
import { logger } from "@/lib/logger";
import { useTour, TourStep } from "@/components/tour/TourProvider";
import { fetchTourFlags, markTourSeen } from "@/lib/tourProgress";

const cutoffDays: Record<string, number> = { week: 7, month: 30, '3months': 90 };

function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { isImpersonating, getEffectiveUserId, impersonatedUser } = useImpersonation();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showOverLimitDialog, setShowOverLimitDialog] = useState(false);
  const [hasCheckedOverLimit, setHasCheckedOverLimit] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("");
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const hasCheckedForReportsRef = useRef(false);
  const isInitialMountRef = useRef(true);
  const { startTour, isActive: tourActive } = useTour();
  const [hasSeenDashboardTour, setHasSeenDashboardTour] = useState<boolean | null>(null);
  
  // State for upselling features
  const [userProfile, setUserProfile] = useState<{ created_at: string; team_id: string | null; full_name: string | null } | null>(null);
  const [totalReportsCreated, setTotalReportsCreated] = useState<number>(0);
  const [dismissedPrompts, setDismissedPrompts] = useState<Set<string>>(new Set());
  const [isStartingTour, setIsStartingTour] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'norths' | 'cmas' | 'buyer-reports'>('all');
  const [isCMADialogOpen, setIsCMADialogOpen] = useState(false);
  const [isBuyerReportDialogOpen, setIsBuyerReportDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft' | 'processing'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'week' | 'month' | '3months'>('all');
  const [agentFilter, setAgentFilter] = useState<string>('all');
  // Resolved name for agent filtering — kept in state so it's ready when the hook is called
  const [agentName, setAgentName] = useState<string | null>(null);

  // Bulk selection — Map<id, type> so cross-type ops work on the "All" tab
  type SelectableType = 'north' | 'cma' | 'buyer-report';
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Map<string, SelectableType>>(new Map());
  const [isBulkLoading, setIsBulkLoading] = useState(false);

  const toggleId = useCallback((id: string, type: SelectableType) => {
    setSelectedIds(prev => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id); else next.set(id, type);
      return next;
    });
  }, []);

  const exitSelectMode = useCallback(() => {
    setIsSelectMode(false);
    setSelectedIds(new Map());
  }, []);

  // Helper function to check if this is a tab return (not initial mount or navigation)
  const isTabReturn = useCallback(() => {
    return sessionStorage.getItem('dashboard-is-tab-return') === 'true';
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Use paginated data hook
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isFetched,
    error
  } = useDashboardDataPaginated(debouncedSearch, sortBy, statusFilter, dateFilter, agentName);

  const { planLimits, usage, subscription, getUsagePercentage, loading: usageLoading, refreshUsage, canCreateCMA, canCreateBuyerReport } = useTeamUsageQuery();
  const { data: cmaList = [], refetch: refetchCMAs } = useCMAListQuery(userProfile?.team_id || undefined);
  const { data: buyerReportList = [], refetch: refetchBuyerReports } = useBuyerReportListQuery(userProfile?.team_id || undefined);

  // Track browser tab visibility to detect tab switches (not navigation)
  // Use sessionStorage to persist across component remounts
  useEffect(() => {
    // Mark initial mount as complete after first render
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab just became hidden - record timestamp in sessionStorage
        sessionStorage.setItem('dashboard-tab-hidden-time', Date.now().toString());
        // Clear any existing tab return flag when tab becomes hidden
        sessionStorage.removeItem('dashboard-is-tab-return');
      } else {
        // Tab became visible - check if this is a tab return
        const hiddenTime = sessionStorage.getItem('dashboard-tab-hidden-time');
        if (hiddenTime) {
          const timeSinceHidden = Date.now() - parseInt(hiddenTime, 10);
          // If tab was hidden within last 10 seconds, mark as tab return
          // Increased window to be more reliable
          if (timeSinceHidden < 10000) {
            sessionStorage.setItem('dashboard-is-tab-return', 'true');
            // Clear the flag after a delay to allow next navigation
            setTimeout(() => {
              sessionStorage.removeItem('dashboard-is-tab-return');
            }, 2000);
          } else {
            // Tab was hidden for too long, treat as navigation
            sessionStorage.removeItem('dashboard-tab-hidden-time');
            sessionStorage.removeItem('dashboard-is-tab-return');
          }
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // If coming from checkout, force refresh subscription data and remove query param
  useEffect(() => {
    // Don't run if this is a tab return
    if (isTabReturn()) {
      return;
    }

    if (searchParams.get('checkout') === 'success') {
      // Force sync from Polar first, then refresh
      const syncAndRefresh = async () => {
        try {
          logger.debug('Syncing from Polar after checkout redirect...');
          // Get effective user ID (impersonated or actual)
          const effectiveUserId = await getEffectiveUserId();
          await supabase.functions.invoke('sync-polar-subscription', {
            body: { effectiveUserId },
          });
          
          // Wait for DB to update and for redirect check query to complete
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // CRITICAL: Clear and refetch ALL subscription-related queries
          await queryClient.invalidateQueries({ queryKey: ['team-usage'] });
          await queryClient.invalidateQueries({ queryKey: ['dashboard-redirect-check'] });
          
          // Refresh usage data
          await refreshUsage();
          
          // Wait a bit more for queries to refetch
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Force refetch redirect check to ensure dashboard recognizes subscription
          await queryClient.refetchQueries({ queryKey: ['dashboard-redirect-check'] });
          
          // Remove the query parameter from URL after everything is synced
          await new Promise(resolve => setTimeout(resolve, 500));
          searchParams.delete('checkout');
          setSearchParams(searchParams, { replace: true });
        } catch (error) {
          logger.error('Error syncing after checkout:', error);
          // Still refresh even if sync fails
          await refreshUsage();
          // Invalidate and refetch redirect check even on error
          await queryClient.invalidateQueries({ queryKey: ['dashboard-redirect-check'] });
          await queryClient.refetchQueries({ queryKey: ['dashboard-redirect-check'] });
          // Remove query param even on error
          searchParams.delete('checkout');
          setSearchParams(searchParams, { replace: true });
        }
      };
      
      syncAndRefresh();
    }
  }, [searchParams, setSearchParams, refreshUsage, queryClient, getEffectiveUserId, isTabReturn]);

  // Load dismissed prompts from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('dismissed-upgrade-prompts');
    if (stored) {
      try {
        setDismissedPrompts(new Set(JSON.parse(stored)));
      } catch (e) {
        logger.error('Error parsing dismissed prompts:', e);
      }
    }
  }, []);

  // Fetch user profile and total reports count
  useEffect(() => {
    // Don't fetch if this is a tab return - use cached data
    if (isTabReturn()) {
      return;
    }

    const fetchUserData = async () => {
      // Double-check not a tab return before fetching
      if (isTabReturn()) {
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user profile with created_at
      const { data: profile } = await supabase
        .from('profiles')
        .select('created_at, team_id, full_name')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        setUserProfile(profile);

        // Get all team members
        if (profile.team_id) {
          const { data: teamProfiles } = await supabase
            .from('profiles')
            .select('user_id')
            .eq('team_id', profile.team_id);

          const teamUserIds = teamProfiles?.map(p => p.user_id) || [];

          // Count total reports ever created (all time, not just current period)
          // Check for both published and unpublished reports
          const { count } = await supabase
            .from('norths')
            .select('*', { count: 'exact', head: true })
            .in('user_id', teamUserIds);

          setTotalReportsCreated(count || 0);
        }
      }
    };

    fetchUserData();
  }, [isTabReturn]);

  useEffect(() => {
    // Don't reload tour flags if this is a tab return
    if (isTabReturn()) {
      return;
    }

    const loadFlags = async () => {
      try {
        const flags = await fetchTourFlags();
        setHasSeenDashboardTour(flags.dashboardSeen);
      } catch (error) {
        logger.error("Failed to load dashboard tour flags", error);
        setHasSeenDashboardTour(true);
      }
    };
    loadFlags();
  }, [isTabReturn]);

  // Show welcome dialog if user has no reports (published or unpublished)
  // DISABLED: Temporarily disabled due to flashing issue when navigating back to dashboard
  // useEffect(() => {
  //   // Wait for data to fully load - check that data exists, has pages structure, and has been fetched
  //   // Also check isFetching to prevent showing dialog during background refetches
  //   if (isLoading || isFetching || !isFetched || !data || !data.pages || data.pages.length === 0) {
  //     setShowWelcomeDialog(false);
  //     return;
  //   }

  //   // Check if user has any reports (published or unpublished)
  //   // Make sure we check the actual first page data, not just if pages array exists
  //   const firstPage = data.pages[0];
  //   const hasReports = (firstPage?.norths && firstPage.norths.length > 0) || 
  //                     data.pages.some(page => page.norths && page.norths.length > 0) || 
  //                     totalReportsCreated > 0;

  //   // Once we've checked and found reports, remember it to prevent dialog from showing again
  //   if (hasReports) {
  //     hasCheckedForReportsRef.current = true;
  //     setShowWelcomeDialog(false);
  //     // Mark that we're no longer on initial mount
  //     isInitialMountRef.current = false;
  //     return;
  //   }

  //   // Only show welcome dialog if:
  //   // 1. No reports exist
  //   // 2. Not impersonating  
  //   // 3. This is the initial mount (not a navigation back)
  //   // 4. We haven't already confirmed the user has reports
  //   // This prevents the dialog from flashing when navigating back from a North report
  //   if (!hasReports && !isImpersonating && isInitialMountRef.current && !hasCheckedForReportsRef.current) {
  //     setShowWelcomeDialog(true);
  //     // Mark that we've checked on initial mount
  //     isInitialMountRef.current = false;
  //   } else {
  //     setShowWelcomeDialog(false);
  //   }
  // }, [isLoading, isFetching, isFetched, data, totalReportsCreated, isImpersonating]);

  // Ensure welcome dialog is always hidden for now
  useEffect(() => {
    setShowWelcomeDialog(false);
  }, []);

  // Function to open upgrade dialog
  const handleOpenUpgradeDialog = (feature = "") => {
    setUpgradeFeature(feature);
    setShowUpgradeDialog(true);
  };

  // Function to open billing portal (kept for other uses)
  const handleOpenBillingPortal = async () => {
    try {
      // CRITICAL: Use getEffectiveUserId() which handles impersonation correctly
      const effectiveUserId = await getEffectiveUserId();
      
      if (!effectiveUserId) {
        throw new Error('Not authenticated');
      }
      
      // Get auth user for logging
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      
      const { data, error } = await supabase.functions.invoke('create-polar-portal', {
        body: {
          returnUrl: `${window.location.origin}/dashboard`,
          // CRITICAL: Pass the effective user ID (handles impersonation correctly)
          effectiveUserId: effectiveUserId,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      logger.error('Portal error:', error);
      toast({
        title: 'Portal Access Failed',
        description: error instanceof Error ? error.message : 'Failed to open billing portal',
        variant: 'destructive',
      });
    }
  };

  // Function to dismiss a prompt
  const dismissPrompt = (promptId: string) => {
    const newDismissed = new Set(dismissedPrompts);
    newDismissed.add(promptId);
    setDismissedPrompts(newDismissed);
    localStorage.setItem('dismissed-upgrade-prompts', JSON.stringify(Array.from(newDismissed)));
  };

  // Calculate usage percentage
  const usagePercentage = !usageLoading && usage && planLimits && planLimits.max_active_norths !== null
    ? getUsagePercentage('norths')
    : 0;

  // Determine which upgrade message to show
  const getUpgradeMessage = () => {
    if (usagePercentage >= 90) {
      const promptId = 'upgrade-90';
      if (dismissedPrompts.has(promptId)) return null;
      
      const remaining = Math.max(0, (planLimits?.max_active_norths || 0) - (usage?.norths_created || 0));
      return {
        id: promptId,
        variant: "destructive" as const,
        title: "Almost Out of Reports",
        message: `Only ${remaining} report${remaining !== 1 ? 's' : ''} left! Upgrade now to avoid interruption.`,
        showCTA: true,
      };
    } else if (usagePercentage >= 80) {
      // Keep existing alert - return null so existing alert shows
      return null;
    } else if (usagePercentage >= 70) {
      const promptId = 'upgrade-70';
      if (dismissedPrompts.has(promptId)) return null;
      
      return {
        id: promptId,
        variant: "default" as const,
        title: "Getting Close to Your Limit",
        message: `You've used ${usage?.norths_created || 0} of ${planLimits?.max_active_norths || 0} reports. Consider upgrading to avoid interruption.`,
        showCTA: true,
      };
    } else if (usagePercentage >= 50) {
      const promptId = 'upgrade-50';
      if (dismissedPrompts.has(promptId)) return null;
      return {
        id: promptId,
        variant: "default" as const,
        title: "You're Halfway There",
        message: `You've used ${usage?.norths_created || 0} of your ${planLimits?.max_active_norths || 0} reports.`,
        showCTA: false,
      };
    }
    return null;
  };

  // Check for time-based prompts
  const getTimeBasedPrompt = () => {
    if (!userProfile || !planLimits) return null;

    // Show upgrade prompt for starter plans after 7 days
    const isStarterPlan = planLimits.plan_tier === 'starter';
    if (!isStarterPlan) return null;

    // Use the subscription created_at to measure active usage time, not profile created_at.
    // Profile created_at reflects when the auth account was created, which can be much earlier
    // than when the user actually completed checkout and started using the platform.
    const activeStartDate = (subscription as any)?.created_at || userProfile.created_at;
    const accountAge = activeStartDate
      ? (Date.now() - new Date(activeStartDate).getTime()) / (1000 * 60 * 60 * 24)
      : 0;

    // Show after 7 days on starter plan
    if (accountAge >= 7) {
      const promptId = 'time-based-7days';
      if (dismissedPrompts.has(promptId)) return null;
      
      return {
        id: promptId,
        variant: "default" as const,
        title: "Ready to Scale?",
        message: "You've been using North Reports for a week! Upgrade to Lite for 12 active reports.",
        showCTA: true,
      };
    }

    // Show after creating 5 reports (all time)
    if (totalReportsCreated >= 5) {
      const promptId = 'time-based-5reports';
      if (dismissedPrompts.has(promptId)) return null;
      
      return {
        id: promptId,
        variant: "default" as const,
        title: "You're Active!",
        message: `You've created ${totalReportsCreated} reports. Consider upgrading to Growth for more capacity.`,
        showCTA: true,
      };
    }

    return null;
  };

  const upgradeMessage = getUpgradeMessage();
  const timeBasedPrompt = getTimeBasedPrompt();

  // Flatten all pages into single array
  const allNorths = useMemo(() => {
    return data?.pages.flatMap(page => page.data.norths) || [];
  }, [data]);

  // Get metadata from first page
  const dashboardMeta = useMemo(() => {
    return data?.pages[0]?.meta || {
      secondaryBrandColor: '#1b489b',
      isAdmin: false,
    };
  }, [data]);

  const isAdmin = dashboardMeta.isAdmin;

  // Team members for agent filter dropdown (admin only) — sorted alphabetically
  const { data: teamMembersRaw = [] } = useQuery({
    queryKey: ['team-members-filter', userProfile?.team_id],
    queryFn: async () => {
      const { data: members } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .eq('team_id', userProfile!.team_id!);
      return members || [];
    },
    enabled: isAdmin && !!userProfile?.team_id,
    staleTime: 1000 * 60 * 5,
  });
  const teamMembers = useMemo(() =>
    [...teamMembersRaw].sort((a, b) => (a.full_name ?? '').localeCompare(b.full_name ?? '')),
    [teamMembersRaw]
  );

  // Sync resolved agent name into state so it's available at the top of the next render
  useEffect(() => {
    if (agentFilter === 'all') {
      setAgentName(null);
    } else {
      const name = teamMembers.find(m => m.user_id === agentFilter)?.full_name ?? null;
      setAgentName(name);
    }
  }, [agentFilter, teamMembers]);

  // Clear selection when tab changes
  useEffect(() => { exitSelectMode(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoize hover color calculation
  const hoverColor = useMemo(() => {
    const secondaryBrandColor = dashboardMeta.secondaryBrandColor;
    const lightenColorWithWhite = (hex: string, whitePercentage: number = 50): string => {
      const cleanHex = hex.replace('#', '');
      const r = parseInt(cleanHex.substring(0, 2), 16);
      const g = parseInt(cleanHex.substring(2, 4), 16);
      const b = parseInt(cleanHex.substring(4, 6), 16);
      const whiteAmount = (255 * whitePercentage) / 100;
      const newR = Math.min(255, Math.round(r + whiteAmount));
      const newG = Math.min(255, Math.round(g + whiteAmount));
      const newB = Math.min(255, Math.round(b + whiteAmount));
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    };
    return lightenColorWithWhite(secondaryBrandColor, 50);
  }, [dashboardMeta.secondaryBrandColor]);

  const filteredCMAs = useMemo(() => {
    let list = cmaList;
    if (statusFilter === 'published')  list = list.filter(c => c.is_published);
    if (statusFilter === 'draft')      list = list.filter(c => !c.is_published);
    if (statusFilter === 'processing') list = []; // CMAs have no processing state
    if (dateFilter !== 'all') {
      const cutoff = new Date(Date.now() - cutoffDays[dateFilter] * 86400000);
      list = list.filter(c => new Date(c.created_at) >= cutoff);
    }
    if (agentName) list = list.filter(c => c.custom_agent_name?.toLowerCase() === agentName.toLowerCase());
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter(c =>
        c.address?.toLowerCase().includes(q) ||
        c.city?.toLowerCase().includes(q) ||
        c.seller_name?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [cmaList, statusFilter, dateFilter, agentName, debouncedSearch]);

  const filteredBuyerReports = useMemo(() => {
    let list = buyerReportList;
    if (statusFilter === 'published')  list = list.filter(r => r.is_published);
    if (statusFilter === 'draft')      list = list.filter(r => !r.is_published);
    if (statusFilter === 'processing') list = []; // Buyer Reports have no processing state
    if (dateFilter !== 'all') {
      const cutoff = new Date(Date.now() - cutoffDays[dateFilter] * 86400000);
      list = list.filter(r => new Date(r.created_at) >= cutoff);
    }
    if (agentName) list = list.filter(r => r.custom_agent_name?.toLowerCase() === agentName.toLowerCase());
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter(r =>
        r.address?.toLowerCase().includes(q) ||
        r.city?.toLowerCase().includes(q) ||
        r.buyer_name?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [buyerReportList, statusFilter, dateFilter, agentName, debouncedSearch]);

  const welcomeIndexRef = useRef(Math.floor(Math.random() * 10));
  const welcomeMessage = useMemo(() => {
    const firstName = userProfile?.full_name?.trim().split(/\s+/)[0];
    if (!firstName) return "Welcome back!";
    const hour = new Date().getHours();
    const tod = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
    const messages = [
      `Good ${tod}, ${firstName}! Ready to close some deals?`,
      `Hey ${firstName} — let's win some listings today.`,
      `Welcome back, ${firstName}. Your sellers are counting on you.`,
      `Good ${tod}, ${firstName}. Let's make today count.`,
      `What's up, ${firstName}? Time to make some moves.`,
      `Back at it, ${firstName}. Big things are happening.`,
      `Hey ${firstName}, time to wow some sellers.`,
      `Good ${tod}, ${firstName}. Doors are opening.`,
      `Let's get it, ${firstName}. The market doesn't wait.`,
      `Good to see you, ${firstName}. Let's build something great.`,
    ];
    return messages[welcomeIndexRef.current % messages.length];
  }, [userProfile?.full_name]);

  const dashboardTourSteps = useMemo<TourStep[]>(() => [
    {
      id: "create",
      title: "🚀 Create your first North",
      description: "Kick off a new seller report from here. We'll guide you through the builder so you can publish fast.",
      selector: '[data-tour="dashboard-create"]',
      placement: "left",
    },
    {
      id: "create-cma",
      title: "📊 Start with a CMA",
      description: "Before you win the listing, create a Comparative Market Analysis. Pull comps, get an AI pricing recommendation, and share a branded report with your seller. When you win the listing, convert it to a North Report with one click.",
      selector: '[data-tour="dashboard-create"]',
      placement: "left",
    },
    {
      id: "create-buyer-report",
      title: "🏠 Run a Buyer Report",
      description: "Shopping for a home with a buyer? Run a Buyer Report on any property they're considering. You'll get price history, school ratings, walkability scores, comparable sales, and an AI-powered offer strategy — then share a branded report link directly with your buyer so they can make a confident decision.",
      selector: '[data-tour="dashboard-create"]',
      placement: "left",
    },
    {
      id: "search",
      title: "🔍 Find anything quickly",
      description: "Search by address, city, or seller name across both your CMAs and North Reports. Use the filter pills to switch between views.",
      selector: '[data-tour="dashboard-search"]',
      placement: "bottom",
    },
    {
      id: "first-card",
      title: "📂 Your reports",
      description: "Each card shows the property image, status, and seller name at a glance. Click the address to open it.",
      selector: '[data-tour="dashboard-first-card"]',
      placement: "top",
    },
    {
      id: "legend",
      title: "✨ Actions at a glance",
      description: (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            <span>Preview and make changes</span>
          </div>
          <div className="flex items-center gap-2">
            <Pencil className="h-4 w-4" />
            <span>Edit names</span>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-green-600" />
            <span>Published</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="relative inline-flex items-center justify-center h-4 w-4">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <X className="h-4 w-4 absolute text-muted-foreground rotate-45" />
            </span>
            <span>Unpublished</span>
          </div>
          <div className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4" />
            <span>Copy shareable link</span>
          </div>
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4" />
            <span>Convert CMA to North Report</span>
          </div>
          <div className="flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-destructive" />
            <span>Delete</span>
          </div>
        </div>
      ),
      selector: '[data-tour="dashboard-card-actions"]',
      placement: "top",
    },
  ], []);

  const startDashboardTour = useCallback(() => {
    if (!dashboardTourSteps.length || tourActive || isStartingTour) return;
    // Ensure the first step's target element is actually in the DOM before starting
    const firstTarget = dashboardTourSteps[0]?.selector;
    if (firstTarget && !document.querySelector(firstTarget)) return;
    setIsStartingTour(true);
    startTour(dashboardTourSteps, {
      maskPadding: 10,
      onFinish: () => {
        markTourSeen("has_seen_dashboard_tour").finally(() => setIsStartingTour(false));
        setHasSeenDashboardTour(true);
      },
      onExit: () => {
        markTourSeen("has_seen_dashboard_tour").finally(() => setIsStartingTour(false));
        setHasSeenDashboardTour(true);
      }
    });
  }, [dashboardTourSteps, isStartingTour, startTour, tourActive]);

  useEffect(() => {
    if (isLoading || isFetching) return;
    if (hasSeenDashboardTour === false && !tourActive && !isStartingTour) {
      const timer = setTimeout(startDashboardTour, 400);
      return () => clearTimeout(timer);
    }
  }, [hasSeenDashboardTour, tourActive, isStartingTour, startDashboardTour, isLoading, isFetching]);

  // Removed - now using React Query cached check above

  // Check if user is over their plan limit and show dialog
  useEffect(() => {
    // Don't check on tab return - only check on initial load or when usage changes
    if (isTabReturn()) {
      return;
    }

    if (!usageLoading && usage && planLimits && !hasCheckedOverLimit) {
      setHasCheckedOverLimit(true);
      if (
        planLimits.max_active_norths !== null &&
        usage.norths_created > planLimits.max_active_norths
      ) {
        setShowOverLimitDialog(true);
      }
    }
  }, [usageLoading, usage, planLimits, hasCheckedOverLimit, isTabReturn]);

  const handleOverLimitResolved = () => {
    // Refresh the usage data and dashboard
    refreshUsage();
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  // Track if we're coming from checkout to delay redirect check
  const isFromCheckout = searchParams.get('checkout') === 'success';
  // Track if we're coming from invite acceptance — profile/role writes may still be in flight
  const isFromInviteAcceptance = searchParams.get('invite') === 'accepted';

  // Cache admin/subscription check with React Query to avoid repeated DB calls
  const { data: redirectCheck, isLoading: isRedirectCheckLoading } = useQuery({
    queryKey: ['dashboard-redirect-check', isImpersonating, isFromCheckout, isFromInviteAcceptance],
    queryFn: async () => {
      // Use getSession() (cached, no network call) instead of getUser() (network call)
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return { shouldRedirect: false };

      // For impersonation, use the impersonated user ID; otherwise use session user
      const effectiveUserId = (isImpersonating && impersonatedUser?.id)
        ? impersonatedUser.id
        : user.id;

      // Check admin FIRST before any profile/subscription queries
      if (user.email === 'admin@northreports.com' && !isImpersonating) {
        return { shouldRedirect: true, redirectTo: '/admin' };
      }

      // Fetch profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('team_id, created_at, email')
        .eq('user_id', effectiveUserId)
        .maybeSingle();

      if (profileError) {
        logger.error('Error fetching profile for redirect check:', profileError);
        return { shouldRedirect: false };
      }

      if (profile?.team_id) {
        // Run role check, invitation check, and subscription check in parallel
        const [roleResult, invitationResult, subscriptionResult] = await Promise.all([
          supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', effectiveUserId)
            .maybeSingle(),
          supabase
            .from('team_invitations')
            .select('id, status')
            .ilike('email', profile.email || user.email || '')
            .in('status', ['accepted', 'pending'])
            .maybeSingle(),
          supabase
            .from('team_subscriptions')
            .select('plan_tier, status, current_period_end, polar_subscription_id, trial_ends_at')
            .eq('team_id', profile.team_id)
            .maybeSingle(),
        ]);

        // Agent role users never get redirected to select-plan — they share the team's subscription
        if (roleResult.data?.role === 'agent') {
          return { shouldRedirect: false };
        }

        // Invited users stay on dashboard — team subscription is handled by the owner
        if (invitationResult.data) {
          return { shouldRedirect: false };
        }

        if (subscriptionResult.error) {
          logger.error('Error fetching subscription for redirect check:', subscriptionResult.error);
          return { shouldRedirect: false };
        }

        let subscription = subscriptionResult.data;

        if (!subscription) {
          // Only poll for webhook arrival during checkout flow or very recent signups
          // Normal page loads trust the DB immediately — reconciliation cron catches misses
          if (isFromCheckout) {
            logger.debug('No subscription found after checkout, polling for webhook...');
            let attempts = 10;
            while (attempts > 0) {
              await new Promise(resolve => setTimeout(resolve, 1000));
              const { data: polled } = await supabase
                .from('team_subscriptions')
                .select('plan_tier, status, current_period_end, polar_subscription_id, trial_ends_at')
                .eq('team_id', profile.team_id)
                .maybeSingle();
              if (polled) {
                subscription = polled;
                break;
              }
              attempts--;
            }
          } else {
            // Check if this is a very recent signup (< 2 minutes) — webhook may still be in flight
            const accountAge = profile.created_at ? Date.now() - new Date(profile.created_at).getTime() : Infinity;
            if (accountAge < 2 * 60 * 1000) {
              logger.debug('No subscription for recent signup — allowing access, realtime listener will catch it');
              return { shouldRedirect: false };
            }
          }

          if (!subscription) {
            logger.debug('No subscription found, redirecting to select-plan');
            return { shouldRedirect: true, redirectTo: '/select-plan' };
          }
        }

        // Check if subscription provides active access
        const { hasActiveSubscription } = await import('@/utils/subscriptionAccess');
        const hasAccess = hasActiveSubscription(subscription.status, subscription.current_period_end, subscription.trial_ends_at);

        if (!hasAccess) {
          return { shouldRedirect: true, redirectTo: '/subscription-restricted' };
        }
      } else {
        // No team_id — check role and pending invitation
        const [roleCheck, pendingInvitation] = await Promise.all([
          supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', effectiveUserId)
            .maybeSingle(),
          supabase
            .from('team_invitations')
            .select('id, status')
            .ilike('email', profile?.email || user.email || '')
            .in('status', ['accepted', 'pending'])
            .maybeSingle(),
        ]);

        // Agent role users never get redirected to select-plan
        if (roleCheck.data?.role === 'agent') {
          return { shouldRedirect: false };
        }

        if (!pendingInvitation.data) {
          return { shouldRedirect: true, redirectTo: '/select-plan' };
        }
        return { shouldRedirect: false };
      }

      return { shouldRedirect: false };
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const isSubscriptionStatusPending = usageLoading || isRedirectCheckLoading || isFromCheckout;

  // Handle redirect based on cached check
  // CRITICAL: Don't redirect if we're coming from checkout or if redirect check is still loading
  // This prevents redirecting users who just completed checkout before sync completes
  useEffect(() => {
    // Don't redirect if:
    // 1. This is a tab return (don't redirect on tab visibility changes)
    // 2. Coming from checkout (wait for sync to complete)
    // 3. Redirect check is still loading
    // 4. No redirect check data yet
    if (isTabReturn() || isFromCheckout || isFromInviteAcceptance || isRedirectCheckLoading || !redirectCheck) {
      return;
    }

    if (redirectCheck.shouldRedirect && redirectCheck.redirectTo) {
      logger.debug('Redirecting to:', redirectCheck.redirectTo, { reason: 'No subscription found' });
      navigate(redirectCheck.redirectTo, { replace: true });
    }
  }, [redirectCheck, navigate, isFromCheckout, isFromInviteAcceptance, isRedirectCheckLoading, isTabReturn]);

  // Set up realtime subscription for norths changes
  useEffect(() => {
    const channel = supabase
      .channel('norths-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'norths',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['dashboard-paginated'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Set up realtime subscription for team_subscriptions changes
  // This invalidates the redirect check when a subscription is created/updated
  // Fixes issue where users get redirected to select-plan after checkout before webhook arrives
  useEffect(() => {
    const checkSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile?.team_id) return;

      const channel = supabase
        .channel(`team-subscriptions-${profile.team_id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'team_subscriptions',
            filter: `team_id=eq.${profile.team_id}`,
          },
          () => {
            // Invalidate redirect check when subscription changes
            // This allows users to stay on dashboard after checkout completes
            logger.debug('Team subscription changed, invalidating redirect check');
            queryClient.invalidateQueries({ queryKey: ['dashboard-redirect-check'] });
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    const cleanup = checkSubscription();
    return () => {
      cleanup.then(cleanupFn => cleanupFn?.());
    };
  }, [queryClient]);

  const handleNorthUpdate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['dashboard-paginated'] }),
      refreshUsage()
    ]);
  }, [queryClient, refreshUsage]);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const selectAll = () => {
    const next = new Map<string, SelectableType>();
    if (activeTab === 'norths' || activeTab === 'all') allNorths.forEach(n => next.set(n.id, 'north'));
    if (activeTab === 'cmas' || activeTab === 'all') filteredCMAs.forEach(c => next.set(c.id, 'cma'));
    if (activeTab === 'buyer-reports' || activeTab === 'all') filteredBuyerReports.forEach(r => next.set(r.id, 'buyer-report'));
    setSelectedIds(next);
  };

  const handleBulkPublish = async (publish: boolean) => {
    if (selectedIds.size === 0) return;
    setIsBulkLoading(true);
    try {
      const now = new Date().toISOString();
      const northIds   = [...selectedIds].filter(([, t]) => t === 'north').map(([id]) => id);
      const cmaIds     = [...selectedIds].filter(([, t]) => t === 'cma').map(([id]) => id);
      const reportIds  = [...selectedIds].filter(([, t]) => t === 'buyer-report').map(([id]) => id);
      const ops: Promise<unknown>[] = [];
      if (northIds.length)  ops.push(supabase.from('norths').update({ is_published: publish, published_at: publish ? now : null }).in('id', northIds));
      if (cmaIds.length)    ops.push(supabase.from('cmas').update({ is_published: publish, published_at: publish ? now : null }).in('id', cmaIds));
      if (reportIds.length) ops.push(supabase.from('property_reports').update({ is_published: publish, published_at: publish ? now : null }).in('id', reportIds));
      await Promise.all(ops);
      if (northIds.length) await handleNorthUpdate();
      if (cmaIds.length) refetchCMAs();
      if (reportIds.length) refetchBuyerReports();
      toast({ title: `${selectedIds.size} report${selectedIds.size > 1 ? 's' : ''} ${publish ? 'published' : 'unpublished'}` });
      exitSelectMode();
    } catch {
      toast({ title: 'Something went wrong', variant: 'destructive' });
    } finally {
      setIsBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Permanently delete ${selectedIds.size} report${selectedIds.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setIsBulkLoading(true);
    try {
      const northIds   = [...selectedIds].filter(([, t]) => t === 'north').map(([id]) => id);
      const cmaIds     = [...selectedIds].filter(([, t]) => t === 'cma').map(([id]) => id);
      const reportIds  = [...selectedIds].filter(([, t]) => t === 'buyer-report').map(([id]) => id);
      const ops: Promise<unknown>[] = [];
      if (northIds.length)  ops.push(supabase.from('norths').delete().in('id', northIds));
      if (cmaIds.length)    ops.push(supabase.from('cmas').delete().in('id', cmaIds));
      if (reportIds.length) ops.push(supabase.from('property_reports').delete().in('id', reportIds));
      await Promise.all(ops);
      if (northIds.length) await handleNorthUpdate();
      if (cmaIds.length) refetchCMAs();
      if (reportIds.length) refetchBuyerReports();
      toast({ title: `${selectedIds.size} report${selectedIds.size > 1 ? 's' : ''} deleted` });
      exitSelectMode();
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' });
    } finally {
      setIsBulkLoading(false);
    }
  };

  // Selectable card wrapper — overlay for bulk selection mode
  const renderSelectable = (id: string, type: SelectableType, children: React.ReactNode, tourId?: string) => (
    <div
      key={id}
      className={`relative ${isSelectMode && selectedIds.has(id) ? 'ring-2 ring-primary rounded-xl' : ''}`}
      data-tour={tourId}
    >
      {isSelectMode && (
        <div className="absolute inset-0 z-10 rounded-xl cursor-pointer" onClick={() => toggleId(id, type)} />
      )}
      {isSelectMode && (
        <div
          className="absolute top-2.5 left-2.5 z-20 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center shadow-sm cursor-pointer"
          style={{ backgroundColor: selectedIds.has(id) ? 'hsl(var(--primary))' : 'rgba(0,0,0,0.45)' }}
          onClick={(e) => { e.stopPropagation(); toggleId(id, type); }}
        >
          {selectedIds.has(id) && <Check className="h-3.5 w-3.5 text-white" />}
        </div>
      )}
      <div className={isSelectMode ? 'pointer-events-none select-none' : ''}>{children}</div>
    </div>
  );

  // Footer component for infinite scroll
  const Footer = () => {
    if (!hasNextPage) return null;
    return (
      <div className="p-4 text-center">
        {isFetchingNextPage ? (
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        ) : (
          <Button onClick={loadMore} variant="outline">
            Load More
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 py-8">
        {/* Consolidated Alert — highest priority only */}
        {(() => {
          if (!subscription && !isSubscriptionStatusPending) return (
            <Alert className="mb-6 relative pr-32">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>No Active Subscription</AlertTitle>
              <AlertDescription>Select a plan to start creating North Reports.</AlertDescription>
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <Button onClick={() => navigate('/select-plan')} size="sm">Select Plan</Button>
              </div>
            </Alert>
          );
          if (subscription?.status === 'canceled' && !dismissedPrompts.has('subscription-cancelled')) return (
            <Alert className="mb-6 relative pr-28 border-destructive/50 bg-destructive/10">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <AlertTitle className="text-destructive">Subscription Cancelled</AlertTitle>
              <AlertDescription>
                You can view existing reports but can't create new ones.{' '}
                <Link to="/settings?tab=billing" className="underline font-medium">Resubscribe</Link>
                {' '}to continue.
              </AlertDescription>
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <Button variant="ghost" size="sm" onClick={() => dismissPrompt('subscription-cancelled')} className="h-8 px-2">Dismiss</Button>
              </div>
            </Alert>
          );
          if (!usageLoading && usage && planLimits && planLimits.max_active_norths !== null && getUsagePercentage('norths') >= 100) return (
            <Alert variant="destructive" className="mb-6 relative pr-32">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Usage Limit Reached</AlertTitle>
              <AlertDescription>
                You've reached your plan's limit of {planLimits.max_active_norths} active North Reports.{' '}
                <Link to="/settings?tab=billing" className="underline font-medium">Upgrade your plan</Link>
                {' '}or unpublish existing reports to make room.
              </AlertDescription>
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <Button variant="outline" size="sm" onClick={handleOpenUpgradeDialog}>Upgrade</Button>
              </div>
            </Alert>
          );
          if (!usageLoading && usage && planLimits && planLimits.max_active_norths !== null && getUsagePercentage('norths') >= 80) return (
            <Alert className="mb-6 relative pr-28">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Approaching Usage Limit</AlertTitle>
              <AlertDescription>
                {usage.norths_created} of {planLimits.max_active_norths} active reports ({Math.round(getUsagePercentage('norths'))}%).{' '}
                <Link to="/settings?tab=billing" className="underline font-medium">Consider upgrading</Link>.
              </AlertDescription>
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <Button variant="outline" size="sm" onClick={handleOpenUpgradeDialog}>Upgrade</Button>
              </div>
            </Alert>
          );
          const isNewUser = userProfile?.created_at && ((Date.now() - new Date(userProfile.created_at).getTime()) / (1000 * 60 * 60) < 24);
          if (isNewUser && subscription?.status === 'trialing' && !dismissedPrompts.has('welcome-trial')) return (
            <Alert className="mb-6 relative pr-48 border-primary/20 bg-primary/5">
              <AlertTriangle className="h-4 w-4 text-primary" />
              <AlertTitle>Welcome to North Reports!</AlertTitle>
              <AlertDescription>
                You're on a <strong>14-day free trial</strong>. Create up to {planLimits?.max_active_norths || 3} North Reports to get started!
              </AlertDescription>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => { setIsDialogOpen(true); dismissPrompt('welcome-trial'); }}>Create Your First Report</Button>
                <Button variant="ghost" size="sm" onClick={() => dismissPrompt('welcome-trial')} className="h-8 px-2">Dismiss</Button>
              </div>
            </Alert>
          );
          if (timeBasedPrompt) return (
            <Alert variant={timeBasedPrompt.variant} className="mb-6 relative pr-40">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{timeBasedPrompt.title}</AlertTitle>
              <AlertDescription>{timeBasedPrompt.message}</AlertDescription>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                {timeBasedPrompt.showCTA && (
                  <Button variant="outline" size="sm" onClick={() => { dismissPrompt(timeBasedPrompt.id); handleOpenUpgradeDialog(); }}>Upgrade Plan</Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => dismissPrompt(timeBasedPrompt.id)} className="h-8 px-2">Dismiss</Button>
              </div>
            </Alert>
          );
          if (upgradeMessage && !(getUsagePercentage('norths') >= 80)) return (
            <Alert variant={upgradeMessage.variant} className="mb-6 relative pr-40">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{upgradeMessage.title}</AlertTitle>
              <AlertDescription>{upgradeMessage.message}</AlertDescription>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                {upgradeMessage.showCTA && (
                  <Button variant="outline" size="sm" onClick={() => { dismissPrompt(upgradeMessage.id); handleOpenUpgradeDialog(); }}>Upgrade Now</Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => dismissPrompt(upgradeMessage.id)} className="h-8 px-2">Dismiss</Button>
              </div>
            </Alert>
          );
          return null;
        })()}

        {/* Page header */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">{welcomeMessage}</h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="default"
              className="hidden sm:flex"
              onClick={startDashboardTour}
            >
              View tutorial
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="gold"
                  size="default"
                  className="gap-2 md:h-11 md:px-6 md:text-base"
                  disabled={!subscription || subscription?.status === 'canceled'}
                  data-tour="dashboard-create"
                >
                  <Plus className="h-4 w-4" />
                  New
                  <ChevronDown className="h-4 w-4 ml-0.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setIsDialogOpen(true)}>
                  North Report
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  if (!canCreateCMA()) { handleOpenUpgradeDialog("CMAs this month"); return; }
                  setIsCMADialogOpen(true);
                }}>
                  CMA
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  if (!canCreateBuyerReport()) { handleOpenUpgradeDialog("Buyer Reports this month"); return; }
                  setIsBuyerReportDialogOpen(true);
                }}>
                  Buyer Report
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Stats bar */}
        {!usageLoading && subscription && subscription.status !== 'canceled' && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div
              className="bg-card border border-border rounded-xl p-4 cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors"
              onClick={handleOpenUpgradeDialog}
            >
              <p className="text-2xl font-bold text-foreground">
                {planLimits?.max_active_norths !== null ? (
                  <>
                    {usage?.norths_created ?? 0}
                    <span className="text-base font-normal text-muted-foreground"> / {planLimits?.max_active_norths}</span>
                  </>
                ) : (
                  usage?.norths_created ?? 0
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Active Norths</p>
              {planLimits?.max_active_norths !== null && (
                <div className="mt-2">
                  <Progress value={getUsagePercentage('norths')} className="h-1" />
                </div>
              )}
            </div>
            <div
              className="bg-card border border-border rounded-xl p-4 cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors"
              onClick={handleOpenUpgradeDialog}
            >
              <p className="text-2xl font-bold text-foreground">
                {planLimits?.max_cmas_per_month != null ? (
                  <>
                    {usage?.cmas_this_month ?? 0}
                    <span className="text-base font-normal text-muted-foreground"> / {planLimits.max_cmas_per_month}</span>
                  </>
                ) : (
                  usage?.cmas_this_month ?? 0
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">CMAs This Month</p>
              {planLimits?.max_cmas_per_month != null && (
                <div className="mt-2">
                  <Progress
                    value={Math.min(((usage?.cmas_this_month ?? 0) / planLimits.max_cmas_per_month) * 100, 100)}
                    className="h-1"
                  />
                </div>
              )}
            </div>
            <div
              className="bg-card border border-border rounded-xl p-4 cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors"
              onClick={handleOpenUpgradeDialog}
            >
              <p className="text-2xl font-bold text-foreground">
                {planLimits?.max_property_reports_per_month != null ? (
                  <>
                    {usage?.property_reports_this_month ?? 0}
                    <span className="text-base font-normal text-muted-foreground"> / {planLimits.max_property_reports_per_month}</span>
                  </>
                ) : (
                  usage?.property_reports_this_month ?? 0
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Buyer Reports This Month</p>
              {planLimits?.max_property_reports_per_month != null && planLimits.max_property_reports_per_month > 0 && (
                <div className="mt-2">
                  <Progress
                    value={Math.min(((usage?.property_reports_this_month ?? 0) / planLimits.max_property_reports_per_month) * 100, 100)}
                    className="h-1"
                  />
                </div>
              )}
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-2xl font-bold text-foreground">
                {allNorths.reduce((s, n) => s + (n.publicUrlViewsCount || 0), 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Report Views</p>
            </div>
          </div>
        )}

        {/* Tab pills + search + sort + filter — single row, wraps to two on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-6" data-tour="dashboard-search">
          {/* Tab pills */}
          <div className="flex items-center gap-1 bg-muted/60 rounded-lg p-1 flex-shrink-0">
            {(['all', 'norths', 'cmas', 'buyer-reports'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setActiveTab(f)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                  activeTab === f
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {f === 'all' ? 'All' : f === 'norths' ? 'North Reports' : f === 'cmas' ? 'CMAs' : 'Buyer Reports'}
                {f === 'norths' && allNorths.length > 0 && (
                  <span className="ml-1.5 text-[10px] bg-muted text-muted-foreground rounded-full px-1.5 py-0.5">
                    {allNorths.length}{hasNextPage ? '+' : ''}
                  </span>
                )}
                {f === 'cmas' && cmaList.length > 0 && (
                  <span className="ml-1.5 text-[10px] bg-muted text-muted-foreground rounded-full px-1.5 py-0.5">
                    {cmaList.length}
                  </span>
                )}
                {f === 'buyer-reports' && buyerReportList.length > 0 && (
                  <span className="ml-1.5 text-[10px] bg-muted text-muted-foreground rounded-full px-1.5 py-0.5">
                    {buyerReportList.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search + sort + filter — push to right on desktop */}
          <div className="flex items-center gap-2 sm:ml-auto">
            <div className="relative flex-1 sm:flex-initial">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search…"
                className="pl-9 h-9 w-full sm:w-52 placeholder:text-xs"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Sort & filter popover */}
            {(() => {
              const showSort = activeTab !== 'cmas' && activeTab !== 'buyer-reports';
              const activeCount =
                (showSort && sortBy !== 'date' ? 1 : 0) +
                (statusFilter !== 'all' ? 1 : 0) +
                (dateFilter !== 'all' ? 1 : 0) +
                (agentFilter !== 'all' ? 1 : 0);
              return (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 px-3 flex-shrink-0 gap-1.5" data-tour="dashboard-sort">
                      <SlidersHorizontal className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline text-xs">Sort & Filter</span>
                      {activeCount > 0 && (
                        <span className="flex items-center justify-center h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
                          {activeCount}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-56 p-3 space-y-3">
                    {showSort && (
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Sort by</label>
                        <Select value={sortBy} onValueChange={setSortBy}>
                          <SelectTrigger className="h-8 text-sm w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="date">Date Created</SelectItem>
                            <SelectItem value="address">Address</SelectItem>
                            <SelectItem value="status">Status</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {showSort && <div className="border-t border-border" />}

                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Status</label>
                      <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
                        <SelectTrigger className="h-8 text-sm w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All statuses</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Date created</label>
                      <Select value={dateFilter} onValueChange={(v) => setDateFilter(v as typeof dateFilter)}>
                        <SelectTrigger className="h-8 text-sm w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All time</SelectItem>
                          <SelectItem value="week">This week</SelectItem>
                          <SelectItem value="month">This month</SelectItem>
                          <SelectItem value="3months">Last 3 months</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {isAdmin && teamMembers.length > 1 && (
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Agent</label>
                        <Select value={agentFilter} onValueChange={setAgentFilter}>
                          <SelectTrigger className="h-8 text-sm w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All agents</SelectItem>
                            {teamMembers.map(m => (
                              <SelectItem key={m.user_id} value={m.user_id}>
                                {m.full_name || 'Unknown'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {activeCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full h-8 text-muted-foreground text-xs"
                        onClick={() => { setSortBy('date'); setStatusFilter('all'); setDateFilter('all'); setAgentFilter('all'); }}
                      >
                        <X className="h-3 w-3 mr-1" /> Reset all
                      </Button>
                    )}
                  </PopoverContent>
                </Popover>
              );
            })()}

            {/* Select mode toggle */}
            {isSelectMode ? (
              <Button variant="outline" size="sm" className="h-9 px-3 flex-shrink-0 text-xs gap-1.5" onClick={exitSelectMode}>
                <X className="h-3.5 w-3.5" /> Cancel
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="h-9 px-3 flex-shrink-0 text-xs gap-1.5" onClick={() => setIsSelectMode(true)}>
                <CheckSquare className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Select</span>
              </Button>
            )}
          </div>
        </div>

        {/* Unified card grid */}
        <div data-tour="dashboard-table">
          {isLoading && activeTab !== 'cmas' && activeTab !== 'buyer-reports' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-card border border-border rounded-xl overflow-hidden">
                  <Skeleton className="aspect-[16/9] w-full" />
                  <div className="p-4 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <Skeleton className="h-3 w-24 mt-2" />
                  </div>
                  <div className="px-3 py-2 border-t border-border flex gap-1">
                    <Skeleton className="h-8 flex-1 rounded-md" />
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <Skeleton className="h-8 w-8 rounded-md" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="p-8 text-center text-destructive bg-card rounded-xl border border-border">
              Error loading reports. Please try again.
            </div>
          ) : (
            <>
              {(activeTab === 'all' || activeTab === 'cmas') && filteredCMAs.length > 0 && (
                <div className={activeTab === 'all' && allNorths.length > 0 ? 'mb-8' : ''}>
                  {activeTab === 'all' && (
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3">CMAs</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCMAs.map((cma, index) =>
                      renderSelectable(
                        cma.id,
                        'cma',
                        <CMACard cma={cma} onUpdate={() => refetchCMAs()} hoverColor={hoverColor} actionsTourId={index === 0 ? 'dashboard-card-actions' : undefined} />,
                        index === 0 ? 'dashboard-first-card' : undefined
                      )
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'cmas' && filteredCMAs.length === 0 && (
                <div className="text-center py-16 bg-card border border-dashed rounded-xl">
                  <p className="font-medium mb-1">{debouncedSearch ? 'No CMAs match your search' : 'No CMAs yet'}</p>
                  {!debouncedSearch && (
                    <>
                      <p className="text-sm text-muted-foreground mb-4">
                        Create a CMA to win listings with AI-powered pricing analysis.
                      </p>
                      <Button
                        variant="gold"
                        onClick={() => setIsCMADialogOpen(true)}
                        className="gap-2"
                        disabled={!subscription || subscription?.status === 'canceled'}
                      >
                        <Plus className="h-4 w-4" />
                        Create First CMA
                      </Button>
                    </>
                  )}
                </div>
              )}

              {(activeTab === 'all' || activeTab === 'buyer-reports') && filteredBuyerReports.length > 0 && (
                <div className={activeTab === 'all' && (allNorths.length > 0 || filteredCMAs.length > 0) ? 'mb-8' : ''}>
                  {activeTab === 'all' && (
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3">Buyer Reports</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredBuyerReports.map((report) =>
                      renderSelectable(report.id, 'buyer-report', <BuyerReportCard report={report} onUpdate={() => refetchBuyerReports()} hoverColor={hoverColor} />)
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'buyer-reports' && filteredBuyerReports.length === 0 && (
                <div className="text-center py-16 bg-card border border-dashed rounded-xl">
                  <p className="font-medium mb-1">{debouncedSearch ? 'No buyer reports match your search' : 'No Buyer Reports yet'}</p>
                  {!debouncedSearch && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Buyer Reports help buyers understand any listing with price history, schools, walkability, and AI offer strategy.
                    </p>
                  )}
                </div>
              )}

              {(activeTab === 'all' || activeTab === 'norths') && (
                <>
                  {activeTab === 'all' && allNorths.length > 0 && (
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3">North Reports</p>
                  )}
                  {allNorths.length === 0 && !isLoading ? (
                    activeTab === 'norths' && (
                      <div className="text-center py-16 bg-card border border-dashed rounded-xl">
                        <p className="font-medium mb-1">{debouncedSearch ? 'No reports match your search' : 'No North Reports yet'}</p>
                        {!debouncedSearch && (
                          <>
                            <p className="text-sm text-muted-foreground mb-4">Create your first North Report to get started.</p>
                            <Button
                              variant="gold"
                              onClick={() => setIsDialogOpen(true)}
                              className="gap-2"
                              disabled={!subscription || subscription?.status === 'canceled'}
                            >
                              <Plus className="h-4 w-4" />
                              Create North
                            </Button>
                          </>
                        )}
                      </div>
                    )
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {allNorths.map((north, index) => {
                        const isFirstCard = index === 0 && (activeTab === 'norths' || filteredCMAs.length === 0);
                        return renderSelectable(
                          north.id,
                          'north',
                          <NorthCard
                            north={north}
                            onUpdate={handleNorthUpdate}
                            hoverColor={hoverColor}
                            isAdmin={dashboardMeta.isAdmin}
                            actionsTourId={isFirstCard ? 'dashboard-card-actions' : undefined}
                          />,
                          isFirstCard ? 'dashboard-first-card' : undefined
                        );
                      })}
                    </div>
                  )}
                  {hasNextPage && (
                    <div className="mt-6 text-center">
                      {isFetchingNextPage ? (
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      ) : (
                        <Button onClick={loadMore} variant="outline">Load More</Button>
                      )}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'all' && allNorths.length === 0 && filteredCMAs.length === 0 && filteredBuyerReports.length === 0 && !isLoading && (
                <div className="text-center py-16 bg-card border border-dashed rounded-xl">
                  <p className="font-medium mb-1">{debouncedSearch ? 'Nothing matches your search' : 'No reports yet'}</p>
                  {!debouncedSearch && (
                    <div className="flex items-center justify-center gap-3 mt-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="gold"
                            className="gap-2"
                            disabled={!subscription || subscription?.status === 'canceled'}
                          >
                            <Plus className="h-4 w-4" />
                            New
                            <ChevronDown className="h-4 w-4 ml-0.5 opacity-70" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center" className="w-48">
                          <DropdownMenuItem onClick={() => setIsDialogOpen(true)}>
                            North Report
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            if (!canCreateCMA()) { handleOpenUpgradeDialog("CMAs this month"); return; }
                            setIsCMADialogOpen(true);
                          }}>
                            CMA
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            if (!canCreateBuyerReport()) { handleOpenUpgradeDialog("Buyer Reports this month"); return; }
                            setIsBuyerReportDialogOpen(true);
                          }}>
                            Buyer Report
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Floating bulk action bar */}
      {isSelectMode && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-popover border border-border rounded-xl shadow-lg px-4 py-3">
          <span className="text-sm font-medium mr-1">
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'None selected'}
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="text-xs text-muted-foreground"
            onClick={selectAll}
          >
            Select all
          </Button>
          <div className="w-px h-5 bg-border mx-1" />
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => handleBulkPublish(true)}
            disabled={isBulkLoading || selectedIds.size === 0}
          >
            <Globe className="h-3.5 w-3.5" />
            Publish
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => handleBulkPublish(false)}
            disabled={isBulkLoading || selectedIds.size === 0}
          >
            <EyeOff className="h-3.5 w-3.5" />
            Unpublish
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-destructive border-destructive/40 hover:bg-destructive/10"
            onClick={handleBulkDelete}
            disabled={isBulkLoading || selectedIds.size === 0}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
          {isBulkLoading && <Loader2 className="h-4 w-4 animate-spin ml-1" />}
        </div>
      )}

      <CreateNorthDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
      <CreateCMADialog open={isCMADialogOpen} onOpenChange={setIsCMADialogOpen} />
      <CreateBuyerReportDialog open={isBuyerReportDialogOpen} onOpenChange={setIsBuyerReportDialogOpen} />

      {/* Upgrade Dialog */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        currentPlan={planLimits?.plan_tier || 'starter'}
        feature={upgradeFeature}
      />

      {/* Over Limit Dialog - blocks dashboard until resolved */}
      {usage && planLimits && planLimits.max_active_norths !== null && (
        <OverLimitDialog
          open={showOverLimitDialog}
          onOpenChange={setShowOverLimitDialog}
          currentCount={usage.norths_created}
          maxAllowed={planLimits.max_active_norths}
          onUnpublishComplete={handleOverLimitResolved}
        />
      )}

      {/* Welcome Dialog - Shows when user has no reports */}
      <Dialog open={showWelcomeDialog} onOpenChange={(open) => {
        setShowWelcomeDialog(open);
        // Don't store in localStorage - show every time they have no reports
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">
              🎉 Welcome to North Reports!
            </DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              {subscription?.status === 'trialing' ? (
                <>You're on a <strong>14-day free trial</strong> of North. Explore freely and see how powerful our Reports are for your clients.</>
              ) : (
                <>Welcome to North. You're all set, and your clients are going to love the clarity our Reports deliver.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-center text-muted-foreground mb-4">
              Get started by creating your first North Report!
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              size="default"
              onClick={() => {
                setShowWelcomeDialog(false);
              }}
              className="w-full sm:flex-1"
            >
              I'll explore first
            </Button>
            <Button
              size="default"
              onClick={() => {
                setShowWelcomeDialog(false);
                setIsDialogOpen(true);
              }}
              className="w-full sm:flex-1"
            >
              Create My First Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Announcement Widget */}
      <AnnouncementWidget />
    </div>
  );
}

export default Dashboard;
